/**
 * Every currency a business can keep its records in: the active ISO 4217 list.
 *
 * Data, not code, and flat on purpose. The picker searches it, apply stores
 * only the three-letter `code`, and nothing else in the app reads a row.
 *
 * What is NOT here, and why:
 *   fund codes      BOV, CHE, CHW, CLF, COU, MXV, USN, UYI, UYW. Accounting
 *                   units, never a currency a shop trades in.
 *   metals          XAU, XAG, XPD, XPT. A weight, not money.
 *   the specials    XDR, XSU, XUA, XTS, XXX. Reserved by the standard.
 *   withdrawn       DEM, FRF, and the rest. ICU still knows them; a person
 *                   creating a business today must never be offered one.
 *
 * `name` is the English ISO name. A device with full ICU can localise it at
 * render time through `Intl.DisplayNames`; this row is the floor when it
 * cannot. `symbol` is the narrow symbol, which falls back to the code where
 * no short symbol exists. `digits` is the minor unit: 0 for the Yen, 3 for
 * the Dinar, 2 almost everywhere.
 *
 * Generated once from the runtime's ICU tables and then committed. Nothing
 * rebuilds it, because ISO 4217 changes about once a year and a currency
 * appearing or retiring is a decision, not a build step.
 */

export type Currency = {
  readonly code: string;
  readonly name: string;
  /** The narrow symbol, or the code where no short symbol exists. */
  readonly symbol: string;
  /** Minor unit digits. 0 for JPY, 3 for KWD, 2 almost everywhere. */
  readonly digits: number;
};

export const CURRENCIES: readonly Currency[] = [
  { code: 'AED', name: 'United Arab Emirates Dirham', symbol: 'AED', digits: 2 },
  { code: 'AFN', name: 'Afghan Afghani', symbol: '؋', digits: 2 },
  { code: 'ALL', name: 'Albanian Lek', symbol: 'ALL', digits: 2 },
  { code: 'AMD', name: 'Armenian Dram', symbol: '֏', digits: 2 },
  { code: 'ANG', name: 'Netherlands Antillean Guilder', symbol: 'ANG', digits: 2 },
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz', digits: 2 },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$', digits: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: '$', digits: 2 },
  { code: 'AWG', name: 'Aruban Florin', symbol: 'AWG', digits: 2 },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼', digits: 2 },
  { code: 'BAM', name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', digits: 2 },
  { code: 'BBD', name: 'Barbadian Dollar', symbol: '$', digits: 2 },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', digits: 2 },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'BGN', digits: 2 },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'BHD', digits: 3 },
  { code: 'BIF', name: 'Burundian Franc', symbol: 'BIF', digits: 0 },
  { code: 'BMD', name: 'Bermudan Dollar', symbol: '$', digits: 2 },
  { code: 'BND', name: 'Brunei Dollar', symbol: '$', digits: 2 },
  { code: 'BOB', name: 'Bolivian Boliviano', symbol: 'Bs', digits: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', digits: 2 },
  { code: 'BSD', name: 'Bahamian Dollar', symbol: '$', digits: 2 },
  { code: 'BTN', name: 'Bhutanese Ngultrum', symbol: 'BTN', digits: 2 },
  { code: 'BWP', name: 'Botswanan Pula', symbol: 'P', digits: 2 },
  { code: 'BYN', name: 'Belarusian Ruble', symbol: 'BYN', digits: 2 },
  { code: 'BZD', name: 'Belize Dollar', symbol: '$', digits: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: '$', digits: 2 },
  { code: 'CDF', name: 'Congolese Franc', symbol: 'CDF', digits: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', digits: 2 },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$', digits: 0 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', digits: 2 },
  { code: 'COP', name: 'Colombian Peso', symbol: '$', digits: 2 },
  { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡', digits: 2 },
  { code: 'CUP', name: 'Cuban Peso', symbol: '$', digits: 2 },
  { code: 'CVE', name: 'Cape Verdean Escudo', symbol: 'CVE', digits: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', digits: 2 },
  { code: 'DJF', name: 'Djiboutian Franc', symbol: 'DJF', digits: 0 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', digits: 2 },
  { code: 'DOP', name: 'Dominican Peso', symbol: '$', digits: 2 },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'DZD', digits: 2 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', digits: 2 },
  { code: 'ERN', name: 'Eritrean Nakfa', symbol: 'ERN', digits: 2 },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'ETB', digits: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', digits: 2 },
  { code: 'FJD', name: 'Fijian Dollar', symbol: '$', digits: 2 },
  { code: 'FKP', name: 'Falkland Islands Pound', symbol: '£', digits: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', digits: 2 },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾', digits: 2 },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: 'GH₵', digits: 2 },
  { code: 'GIP', name: 'Gibraltar Pound', symbol: '£', digits: 2 },
  { code: 'GMD', name: 'Gambian Dalasi', symbol: 'GMD', digits: 2 },
  { code: 'GNF', name: 'Guinean Franc', symbol: 'FG', digits: 0 },
  { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q', digits: 2 },
  { code: 'GYD', name: 'Guyanese Dollar', symbol: '$', digits: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: '$', digits: 2 },
  { code: 'HNL', name: 'Honduran Lempira', symbol: 'L', digits: 2 },
  { code: 'HTG', name: 'Haitian Gourde', symbol: 'HTG', digits: 2 },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', digits: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', digits: 2 },
  { code: 'ILS', name: 'Israeli New Shekel', symbol: '₪', digits: 2 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', digits: 2 },
  { code: 'IQD', name: 'Iraqi Dinar', symbol: 'IQD', digits: 3 },
  { code: 'IRR', name: 'Iranian Rial', symbol: 'IRR', digits: 2 },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr', digits: 0 },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: '$', digits: 2 },
  { code: 'JOD', name: 'Jordanian Dinar', symbol: 'JOD', digits: 3 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', digits: 0 },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KES', digits: 2 },
  { code: 'KGS', name: 'Kyrgyz Som', symbol: '⃀', digits: 2 },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛', digits: 2 },
  { code: 'KMF', name: 'Comorian Franc', symbol: 'CF', digits: 0 },
  { code: 'KPW', name: 'North Korean Won', symbol: '₩', digits: 2 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', digits: 0 },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'KWD', digits: 3 },
  { code: 'KYD', name: 'Cayman Islands Dollar', symbol: '$', digits: 2 },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸', digits: 2 },
  { code: 'LAK', name: 'Laotian Kip', symbol: '₭', digits: 2 },
  { code: 'LBP', name: 'Lebanese Pound', symbol: 'L£', digits: 2 },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs', digits: 2 },
  { code: 'LRD', name: 'Liberian Dollar', symbol: '$', digits: 2 },
  { code: 'LSL', name: 'Lesotho Loti', symbol: 'LSL', digits: 2 },
  { code: 'LYD', name: 'Libyan Dinar', symbol: 'LYD', digits: 3 },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'MAD', digits: 2 },
  { code: 'MDL', name: 'Moldovan Leu', symbol: 'MDL', digits: 2 },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar', digits: 2 },
  { code: 'MKD', name: 'Macedonian Denar', symbol: 'MKD', digits: 2 },
  { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K', digits: 2 },
  { code: 'MNT', name: 'Mongolian Tugrik', symbol: '₮', digits: 2 },
  { code: 'MOP', name: 'Macanese Pataca', symbol: 'MOP', digits: 2 },
  { code: 'MRU', name: 'Mauritanian Ouguiya', symbol: 'MRU', digits: 2 },
  { code: 'MUR', name: 'Mauritian Rupee', symbol: 'Rs', digits: 2 },
  { code: 'MVR', name: 'Maldivian Rufiyaa', symbol: 'MVR', digits: 2 },
  { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MWK', digits: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', digits: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', digits: 2 },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MZN', digits: 2 },
  { code: 'NAD', name: 'Namibian Dollar', symbol: '$', digits: 2 },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', digits: 2 },
  { code: 'NIO', name: 'Nicaraguan Córdoba', symbol: 'C$', digits: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', digits: 2 },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: 'Rs', digits: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: '$', digits: 2 },
  { code: 'OMR', name: 'Omani Rial', symbol: 'OMR', digits: 3 },
  { code: 'PAB', name: 'Panamanian Balboa', symbol: 'PAB', digits: 2 },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'PEN', digits: 2 },
  { code: 'PGK', name: 'Papua New Guinean Kina', symbol: 'PGK', digits: 2 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', digits: 2 },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: 'Rs', digits: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', digits: 2 },
  { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲', digits: 0 },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'QAR', digits: 2 },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', digits: 2 },
  { code: 'RSD', name: 'Serbian Dinar', symbol: 'RSD', digits: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', digits: 2 },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'RF', digits: 0 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'SAR', digits: 2 },
  { code: 'SBD', name: 'Solomon Islands Dollar', symbol: '$', digits: 2 },
  { code: 'SCR', name: 'Seychellois Rupee', symbol: 'SCR', digits: 2 },
  { code: 'SDG', name: 'Sudanese Pound', symbol: 'SDG', digits: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', digits: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: '$', digits: 2 },
  { code: 'SHP', name: 'St. Helena Pound', symbol: '£', digits: 2 },
  { code: 'SLE', name: 'Sierra Leonean Leone', symbol: 'SLE', digits: 2 },
  { code: 'SOS', name: 'Somali Shilling', symbol: 'SOS', digits: 2 },
  { code: 'SRD', name: 'Surinamese Dollar', symbol: '$', digits: 2 },
  { code: 'SSP', name: 'South Sudanese Pound', symbol: '£', digits: 2 },
  { code: 'STN', name: 'São Tomé & Príncipe Dobra', symbol: 'Db', digits: 2 },
  { code: 'SVC', name: 'Salvadoran Colón', symbol: 'SVC', digits: 2 },
  { code: 'SYP', name: 'Syrian Pound', symbol: '£', digits: 2 },
  { code: 'SZL', name: 'Swazi Lilangeni', symbol: 'SZL', digits: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', digits: 2 },
  { code: 'TJS', name: 'Tajikistani Somoni', symbol: 'TJS', digits: 2 },
  { code: 'TMT', name: 'Turkmenistani Manat', symbol: 'TMT', digits: 2 },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'TND', digits: 3 },
  { code: 'TOP', name: 'Tongan Paʻanga', symbol: 'T$', digits: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', digits: 2 },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', symbol: '$', digits: 2 },
  { code: 'TWD', name: 'New Taiwan Dollar', symbol: '$', digits: 2 },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TZS', digits: 2 },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴', digits: 2 },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'UGX', digits: 0 },
  { code: 'USD', name: 'US Dollar', symbol: '$', digits: 2 },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$', digits: 2 },
  { code: 'UZS', name: 'Uzbekistani Som', symbol: 'UZS', digits: 2 },
  { code: 'VED', name: 'Bolívar Soberano', symbol: 'VED', digits: 2 },
  { code: 'VES', name: 'Venezuelan Bolívar', symbol: 'VES', digits: 2 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', digits: 0 },
  { code: 'VUV', name: 'Vanuatu Vatu', symbol: 'VUV', digits: 0 },
  { code: 'WST', name: 'Samoan Tala', symbol: 'WST', digits: 2 },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA', digits: 0 },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: '$', digits: 2 },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'F CFA', digits: 0 },
  { code: 'XPF', name: 'CFP Franc', symbol: 'CFPF', digits: 0 },
  { code: 'YER', name: 'Yemeni Rial', symbol: 'YER', digits: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', digits: 2 },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK', digits: 2 },
  { code: 'ZWG', name: 'Zimbabwean Gold', symbol: 'ZWG', digits: 2 },
];

/**
 * What the picker offers before anybody types.
 *
 * The launch market first, then the currencies its shopkeepers are most often
 * paid in. A plain list, so the order is a decision somebody can argue with
 * rather than a ranking nobody can see.
 */
export const COMMON_CURRENCY_CODES: readonly string[] = [
  'INR', 'USD', 'EUR', 'GBP', 'AED', 'SAR', 'SGD', 'AUD', 'CAD', 'BDT', 'LKR', 'NPR',
];

const BY_CODE: ReadonlyMap<string, Currency> = new Map(
  CURRENCIES.map((currency) => [currency.code, currency]),
);

export const currencyFor = (code: string): Currency | undefined =>
  BY_CODE.get(code.trim().toUpperCase());

export const isCurrencyCode = (code: string): boolean => BY_CODE.has(code.trim().toUpperCase());

/**
 * The rows a query matches, best first.
 *
 * An empty query answers the common list, so the sheet opens on something
 * useful rather than on Afghani. Otherwise: a code that starts with the query
 * first, then a name that starts with it, then anything that contains it.
 * Three tiers and not a score, because a person typing "IN" wants INR at the
 * top and can see why it is there.
 */
export const searchCurrencies = (query: string): readonly Currency[] => {
  const needle = query.trim().toUpperCase();
  if (needle.length === 0) {
    return COMMON_CURRENCY_CODES.map((code) => BY_CODE.get(code)).filter(
      (currency): currency is Currency => currency !== undefined,
    );
  }
  const codeStarts: Currency[] = [];
  const nameStarts: Currency[] = [];
  const contains: Currency[] = [];
  for (const currency of CURRENCIES) {
    const name = currency.name.toUpperCase();
    if (currency.code.startsWith(needle)) codeStarts.push(currency);
    else if (name.startsWith(needle)) nameStarts.push(currency);
    else if (name.includes(needle) || currency.symbol.toUpperCase().includes(needle)) {
      contains.push(currency);
    }
  }
  return [...codeStarts, ...nameStarts, ...contains];
};
